import {
  BackSide,
  Color,
  Mesh,
  ShaderMaterial,
  SphereGeometry,
  Vector3,
} from "three";

import type {
  EnvironmentFrameState,
  EnvironmentSeason,
} from "./environment.ts";
import type { RenderVector3 } from "./types.ts";

export interface AtmosphericSkySeasonProfile {
  readonly upperSky: number;
  readonly cloudLight: number;
  readonly cloudShadow: number;
  readonly cloudCoverage: number;
  readonly cloudDensity: number;
  readonly cloudScale: number;
  readonly hazeStrength: number;
}

export interface AtmosphericSkyConfig {
  readonly radiusMeters: number;
  readonly sunDiskSize: number;
  readonly moonDiskSize: number;
  readonly starDensity: number;
  readonly seasons: Readonly<
    Record<EnvironmentSeason, AtmosphericSkySeasonProfile>
  >;
}

export interface AtmosphericSkyDome {
  readonly object: Mesh<SphereGeometry, ShaderMaterial>;
  update(
    focus: Readonly<RenderVector3>,
    frame: Readonly<EnvironmentFrameState>,
    zenith: Readonly<Color>,
    horizon: Readonly<Color>,
    activeLightPosition: Readonly<RenderVector3>,
  ): void;
  dispose(): void;
}

const DUSK_GLOW = new Color(0xe7a06f);
const NIGHT_CLOUD_LIGHT = new Color(0x667789);
const NIGHT_CLOUD_SHADOW = new Color(0x273545);
const SUN_LIGHT = new Color(0xffedcf);
const DUSK_SUN_LIGHT = new Color(0xffb06f);
const MOON_LIGHT = new Color(0xcdddf1);

function dayVisibility(frame: Readonly<EnvironmentFrameState>): number {
  return Math.max(0, 1 - frame.nightFactor * 1.18);
}

/**
 * Render-only atmosphere. The shader intentionally uses broad cloud forms and
 * muted colour relationships so the sky feels natural without fighting the
 * project's miniature art direction or requiring photographic sky assets.
 */
export function createAtmosphericSkyDome(
  config: Readonly<AtmosphericSkyConfig>,
): AtmosphericSkyDome {
  const geometry = new SphereGeometry(config.radiusMeters, 32, 18);
  const material = new ShaderMaterial({
    name: "vilnius-procedural-seasonal-sky",
    side: BackSide,
    depthWrite: false,
    depthTest: false,
    fog: false,
    uniforms: {
      zenithColor: { value: new Color(0x91a9ad) },
      upperSkyColor: { value: new Color(0x6f95a4) },
      horizonColor: { value: new Color(0xaab5ad) },
      duskGlowColor: { value: DUSK_GLOW.clone() },
      cloudLightColor: { value: new Color(0xe2e4d9) },
      cloudShadowColor: { value: new Color(0x88999b) },
      celestialColor: { value: SUN_LIGHT.clone() },
      celestialDirection: { value: new Vector3(-0.4, 0.8, -0.2).normalize() },
      duskFactor: { value: 0 },
      nightFactor: { value: 0 },
      cloudCoverage: { value: 0.34 },
      cloudDensity: { value: 0.62 },
      cloudScale: { value: 2.8 },
      hazeStrength: { value: 0.2 },
      diskSize: { value: config.sunDiskSize },
      starDensity: { value: config.starDensity },
      skyTime: { value: 0 },
    },
    vertexShader: `
      varying vec3 vSkyDirection;

      void main() {
        vSkyDirection = normalize(position);
        vec4 clipPosition = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        gl_Position = clipPosition.xyww;
      }
    `,
    fragmentShader: `
      uniform vec3 zenithColor;
      uniform vec3 upperSkyColor;
      uniform vec3 horizonColor;
      uniform vec3 duskGlowColor;
      uniform vec3 cloudLightColor;
      uniform vec3 cloudShadowColor;
      uniform vec3 celestialColor;
      uniform vec3 celestialDirection;
      uniform float duskFactor;
      uniform float nightFactor;
      uniform float cloudCoverage;
      uniform float cloudDensity;
      uniform float cloudScale;
      uniform float hazeStrength;
      uniform float diskSize;
      uniform float starDensity;
      uniform float skyTime;

      varying vec3 vSkyDirection;

      float hash21(vec2 point) {
        vec3 point3 = fract(vec3(point.xyx) * 0.1031);
        point3 += dot(point3, point3.yzx + 33.33);
        return fract((point3.x + point3.y) * point3.z);
      }

      float valueNoise(vec2 point) {
        vec2 cell = floor(point);
        vec2 local = fract(point);
        local = local * local * (3.0 - 2.0 * local);
        float a = hash21(cell);
        float b = hash21(cell + vec2(1.0, 0.0));
        float c = hash21(cell + vec2(0.0, 1.0));
        float d = hash21(cell + vec2(1.0, 1.0));
        return mix(mix(a, b, local.x), mix(c, d, local.x), local.y);
      }

      float cloudNoise(vec2 point) {
        float result = 0.0;
        float amplitude = 0.56;
        mat2 rotation = mat2(0.80, -0.60, 0.60, 0.80);
        for (int octave = 0; octave < 5; octave += 1) {
          result += valueNoise(point) * amplitude;
          point = rotation * point * 2.03 + 8.17;
          amplitude *= 0.48;
        }
        return result;
      }

      float starField(vec3 direction) {
        const float PI = 3.14159265359;
        vec2 spherical = vec2(
          atan(direction.z, direction.x) / (2.0 * PI) + 0.5,
          asin(clamp(direction.y, -1.0, 1.0)) / PI + 0.5
        );
        vec2 grid = spherical * vec2(920.0, 460.0);
        vec2 cell = floor(grid);
        vec2 local = fract(grid) - 0.5;
        float seed = hash21(cell);
        float point = 1.0 - smoothstep(0.025, 0.16, length(local));
        float twinkle = 0.72 + 0.28 * sin(seed * 91.0 + skyTime * 0.18);
        return point * step(1.0 - starDensity, seed) * twinkle;
      }

      void main() {
        vec3 direction = normalize(vSkyDirection);
        float altitude = clamp(direction.y, -0.08, 1.0);
        float horizonBand = exp(-abs(direction.y) * 8.2);
        float verticalBlend = smoothstep(0.02, 0.72, altitude);
        float upperBlend = smoothstep(0.52, 0.96, altitude);

        vec3 sky = mix(horizonColor, zenithColor, verticalBlend);
        sky = mix(sky, upperSkyColor, upperBlend * 0.72);
        sky = mix(sky, duskGlowColor, duskFactor * horizonBand * 0.58);

        float celestialDot = max(dot(direction, normalize(celestialDirection)), 0.0);
        float celestialDisk = smoothstep(1.0 - diskSize, 1.0 - diskSize * 0.24, celestialDot);
        float celestialHalo = pow(celestialDot, 96.0) * (0.20 + duskFactor * 0.34);
        sky += celestialColor * (celestialDisk * 0.92 + celestialHalo);

        float projectionHeight = max(0.16, direction.y + 0.36);
        vec2 cloudPoint = direction.xz / projectionHeight;
        cloudPoint = cloudPoint * cloudScale + vec2(skyTime * 0.018, skyTime * 0.006);
        float broadCloud = cloudNoise(cloudPoint);
        float fineCloud = cloudNoise(cloudPoint * 1.84 + 19.4);
        float cloudShape = broadCloud * 0.78 + fineCloud * 0.22;
        float cloudThreshold = 1.0 - cloudCoverage;
        float cloud = smoothstep(
          cloudThreshold - 0.11,
          cloudThreshold + 0.12,
          cloudShape
        );
        float upperHemisphere = smoothstep(-0.02, 0.16, direction.y);
        float horizonCloudFade = smoothstep(-0.01, 0.055, direction.y);
        cloud *= upperHemisphere * horizonCloudFade * cloudDensity;

        float cloudLight = smoothstep(-0.15, 0.76, dot(direction, normalize(celestialDirection)));
        vec3 cloudColor = mix(cloudShadowColor, cloudLightColor, 0.42 + cloudLight * 0.48);
        cloudColor = mix(cloudColor, horizonColor, horizonBand * hazeStrength * 0.5);

        float stars = starField(direction) * nightFactor * (1.0 - cloud);
        sky += vec3(0.72, 0.82, 1.0) * stars * 0.78;
        sky = mix(sky, cloudColor, clamp(cloud, 0.0, 0.92));

        float atmosphericHaze = horizonBand * hazeStrength;
        sky = mix(sky, horizonColor, atmosphericHaze * 0.42);
        gl_FragColor = vec4(sky, 1.0);
      }
    `,
  });

  const object = new Mesh(geometry, material);
  object.name = "vilnius-atmospheric-sky-dome";
  object.frustumCulled = false;
  object.renderOrder = -1000;

  const lightDirection = new Vector3();
  const upperSky = new Color();
  const cloudLight = new Color();
  const cloudShadow = new Color();
  const celestial = new Color();

  return {
    object,
    update(focus, frame, zenith, horizon, activeLightPosition): void {
      const season = config.seasons[frame.season];
      const daylight = dayVisibility(frame);
      object.position.set(focus.x, focus.y, focus.z);

      upperSky
        .copy(zenith)
        .lerp(new Color(season.upperSky), daylight * 0.58);
      cloudLight
        .set(season.cloudLight)
        .lerp(NIGHT_CLOUD_LIGHT, frame.nightFactor * 0.86);
      cloudShadow
        .set(season.cloudShadow)
        .lerp(NIGHT_CLOUD_SHADOW, frame.nightFactor * 0.9);
      celestial
        .copy(SUN_LIGHT)
        .lerp(DUSK_SUN_LIGHT, frame.duskFactor)
        .lerp(MOON_LIGHT, frame.nightFactor);
      lightDirection.set(
        activeLightPosition.x - focus.x,
        activeLightPosition.y - focus.y,
        activeLightPosition.z - focus.z,
      ).normalize();

      material.uniforms.zenithColor?.value.copy(zenith);
      material.uniforms.upperSkyColor?.value.copy(upperSky);
      material.uniforms.horizonColor?.value.copy(horizon);
      material.uniforms.cloudLightColor?.value.copy(cloudLight);
      material.uniforms.cloudShadowColor?.value.copy(cloudShadow);
      material.uniforms.celestialColor?.value.copy(celestial);
      material.uniforms.celestialDirection?.value.copy(lightDirection);
      material.uniforms.duskFactor!.value = frame.duskFactor;
      material.uniforms.nightFactor!.value = frame.nightFactor;
      material.uniforms.cloudCoverage!.value = season.cloudCoverage;
      material.uniforms.cloudDensity!.value = season.cloudDensity;
      material.uniforms.cloudScale!.value = season.cloudScale;
      material.uniforms.hazeStrength!.value = season.hazeStrength;
      material.uniforms.diskSize!.value = frame.nightFactor > 0.5
        ? config.moonDiskSize
        : config.sunDiskSize;
      material.uniforms.skyTime!.value = frame.timeOfDayHours * 60;
    },
    dispose(): void {
      geometry.dispose();
      material.dispose();
      object.removeFromParent();
    },
  };
}
